import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { syncAcceptLanguageHeader } from '@/config/axios';
import { getBrowserLocale } from '@/hooks/useLocale';
import { resolveCatalogOther } from '@/i18n/catalogLookup';
import { loadLocaleBundle } from '@/i18n/languages';

// English is always bundled — it's the fallback and the most common locale.
// All other locales are dynamically imported as separate chunks, loaded on demand.
import en from './locales/hireport-en.json';

const lng = getBrowserLocale();
syncAcceptLanguageHeader(lng);

const isUiSnakeKey = (key: string) =>
  /^[A-Z][A-Z0-9_]*(-[A-Z0-9_-]+)?$/.test(key);

void i18n.use(initReactI18next).init({
  resources: { en: { translation: en } },
  lng,
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false, // React already escapes values to prevent XSS
  },
});

const baseT = i18n.t.bind(i18n);
i18n.t = ((...args: Parameters<typeof baseT>) => {
  const result = baseT(...args);
  const key = args[0];

  if (typeof key === 'string' && result === key) {
    if (!key.startsWith('ats.') && !isUiSnakeKey(key)) {
      const catalogHit = resolveCatalogOther(i18n, key);
      if (catalogHit !== key) {
        return catalogHit;
      }
    }
  }

  return result;
}) as typeof i18n.t;

async function loadLocale(code: string): Promise<void> {
  const bundle = await loadLocaleBundle(code);
  i18n.addResourceBundle(code, 'translation', bundle);
}

if (lng !== 'en') {
  void loadLocale(lng).then(() => {
    void i18n.changeLanguage(lng);
  });
}

// On language switch, lazily load the new locale if it hasn't been loaded yet.
i18n.on('languageChanged', (newLng) => {
  if (newLng !== 'en' && !i18n.hasResourceBundle(newLng, 'translation')) {
    void loadLocale(newLng);
  }
});

export default i18n;
